import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, InputNumber, Button, Card, Row, Col, message, Steps,
  Typography, Alert, Space, Tag, Spin, Upload, DatePicker } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, DeleteOutlined, UploadOutlined,
  StarFilled, StarOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import CabecalhoLaudo from '../components/CabecalhoLaudo';

// Laudo de teste de estanqueidade pelo metodo de bolha. Identificacao e
// equipamento iguais aos do teste hidrostatico; o teste registra so o resultado.

const { Text } = Typography;
const { Option } = Select;

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const aj = (t) => <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2, lineHeight: 1.3 }}>{t}</div>;
const UNI_P = [['kgf', 'kgf/cm²'], ['psi', 'psi'], ['bar', 'bar'], ['mpa', 'MPa']];
const EQUIPAMENTOS = ['Tubulação', 'Tanque', 'Vaso de Pressão', 'Autoclave', 'Caldeira', 'Mangueira', 'Outro'];

// Periodicidade de inspecao geral de tanques em funcao do produto (tabela usada
// nos laudos de tanque). O campo sempre vem com 1 ano; isto e so a recomendacao.
const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const PERIODICIDADE = [
  { anos: 5, chaves: ['diesel', 'querosene', 'gasolina', 'residuos nao tratados', 'residuo nao tratado'],
    grupo: 'Diesel, querosene, gasolina, resíduos não tratados' },
  { anos: 8, chaves: ['residuos leves', 'residuo leve', 'alcool', 'etanol'],
    grupo: 'Resíduos leves tratados, álcool' },
  { anos: 10, chaves: ['oleo combustivel', 'oleos combustiveis', 'lubrificante', 'oleo lubrificante', 'produtos pesados', 'oleo pesado', 'oleo'],
    grupo: 'Óleos combustíveis, lubrificantes e produtos pesados' },
];
const recomendacao = (fluido) => {
  const f = semAcento(fluido);
  if (!f) return null;
  return PERIODICIDADE.find((p) => p.chaves.some((c) => f.indexOf(c) >= 0)) || null;
};
const maisUmAno = (data) => (data ? dayjs(String(data).substring(0, 10)).add(1, 'year').format('YYYY-MM-DD') : null);

export default function TesteEstanqueidade() {
  const nav = useNavigate();
  const { id } = useParams();
  const BASE = '/testes-estanqueidade/';

  const [d, setD] = useState(null);
  const naoSalvo = useAvisoAlteracoes(d);
  const [step, setStep] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [fluidos, setFluidos] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [imgs, setImgs] = useState({});
  const [enviando, setEnviando] = useState(false);

  const set = (campo, v) => setD((x) => {
    const n = { ...x, [campo]: v };
    // proximo teste acompanha a data do teste enquanto ninguem o mexer (= data + 1 ano)
    if (campo === 'data_inspecao' && (!x.proximo_teste || x.proximo_teste === maisUmAno(x.data_inspecao))) {
      n.proximo_teste = maisUmAno(v);
    }
    return n;
  });

  // miniaturas: guardadas pelo nome do arquivo (nao recarrega as que ja estao na tela),
  // reduzidas no servidor (?mini=1) e no maximo 4 de cada vez
  const fila = React.useRef({ pend: [], ativos: 0, vistos: {} });
  const proxMini = useCallback(() => {
    const q = fila.current;
    while (q.ativos < 4 && q.pend.length) {
      const { idx, arq } = q.pend.shift();
      q.ativos++;
      api.get(BASE + id + '/fotos/' + idx + '?mini=1', { responseType: 'blob' })
        .then((r) => setImgs((p) => ({ ...p, [arq]: URL.createObjectURL(r.data) })))
        .catch(() => { delete q.vistos[arq]; })
        .finally(() => { q.ativos--; proxMini(); });
    }
  }, [id]);
  const miniaturas = useCallback((lista) => {
    const q = fila.current;
    (lista || []).forEach((f, idx) => {
      const arq = f.arquivo || f.caminho || String(idx);
      if (q.vistos[arq]) return;
      q.vistos[arq] = true;
      q.pend.push({ idx, arq });
    });
    proxMini();
  }, [proxMini]);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get(BASE + id);
      const l = r.data;
      ['pmta_unidade', 'pmo_unidade', 'pressao_prova_unidade'].forEach((k) => { if (!l[k]) l[k] = 'kgf'; });
      if (!l.pressao_prova_tipo) l.pressao_prova_tipo = 'atm';
      if (!l.status) l.status = 'rascunho';
      if (l.data_inspecao) l.data_inspecao = String(l.data_inspecao).substring(0, 10);
      if (l.proximo_teste) l.proximo_teste = String(l.proximo_teste).substring(0, 10);
      else if (l.data_inspecao) l.proximo_teste = maisUmAno(l.data_inspecao);
      setD(l);
      miniaturas(l.fotos);
    } catch { message.error('Erro ao carregar o laudo'); }
  }, [id, miniaturas]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/classificacao-nr13/fluidos').then((r) => setFluidos(r.data || [])).catch(() => {});
    api.get('/materiais').then((r) => setMateriais(r.data || [])).catch(() => {});
  }, []);

  // depois do PDF: so atualiza codigo e status (as fotos nao mudaram)
  const recarregarFotos = useCallback(async () => {
    try {
      const r = await api.get(BASE + id);
      setD((x) => ({ ...x, codigo: r.data.codigo, status: r.data.status || x.status }));
    } catch { /* mantem */ }
  }, [id]);

  const usarLista = (lista) => {
    setD((p) => ({ ...p, fotos: lista || [] }));
    miniaturas(lista);
  };
  const subir = async (file) => {
    setEnviando(true);
    try {
      const fd = new FormData(); fd.append('foto', file);
      const r = await api.post(BASE + id + '/fotos', fd);
      usarLista(r.data.lista);
    } catch (e) { message.error('Erro ao enviar foto'); }
    finally { setEnviando(false); }
    return false;
  };
  const remover = async (idx) => {
    try { const r = await api.delete(BASE + id + '/fotos/' + idx); usarLista(r.data.lista); }
    catch (e) { message.error('Erro ao remover'); }
  };
  const definirCapa = async (idx) => {
    try { const r = await api.put(BASE + id + '/fotos/' + idx + '/principal'); setD((p) => ({ ...p, fotos: r.data.fotos })); }
    catch (e) { message.error('Erro ao definir a capa'); }
  };

  const salvar = async (aviso = true) => {
    if (!d) return false;
    setSalvando(true);
    try {
      const corpo = { ...d };
      ['id', 'codigo', 'criado_em', 'atualizado_em', 'disponivel_portal', 'app_uuid', 'pdf_assinado_em',
        'razao_social', 'nome_cliente', 'cnpj', 'art_cliente', 'cnpj_faturamento', 'os_numero',
        'logradouro_fiscal', 'numero_fiscal', 'bairro_fiscal', 'cidade_fiscal', 'estado_fiscal', 'cep_fiscal',
        'end_atend_logradouro', 'end_atend_numero', 'end_atend_bairro', 'end_atend_cidade',
        'end_atend_estado', 'end_atend_cep', 'origem']
        .forEach((k) => delete corpo[k]);
      corpo.fotos = (d.fotos || []).map((f) => ({ legenda: f.legenda || '' }));
      await api.put(BASE + id, corpo);
      naoSalvo.limpo();
      if (aviso) message.success('Laudo salvo');
      return true;
    } catch (e) {
      message.error('Erro ao salvar: ' + (e?.response?.data?.erro || e.message));
      return false;
    } finally { setSalvando(false); }
  };

  const gerarPdf = async () => {
    message.loading({ content: 'Gerando o PDF...', key: 'pdf', duration: 0 });
    try {
      if (!(await salvar(false))) { message.destroy('pdf'); return; }
      const r = await api.get(BASE + id + '/pdf', { responseType: 'blob', timeout: 180000 });
      message.destroy('pdf');
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
      await recarregarFotos();
    } catch (e) {
      message.destroy('pdf');
      let msg = 'Erro ao gerar o PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) { /* nao e json */ }
      message.error(msg);
    }
  };

  if (!d) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  const fluidoNome = d.fluido === 'outro' ? d.fluido_outro : d.fluido;
  const rec = recomendacao(fluidoNome);

  const etapas = [{ title: 'Identificação' }, { title: 'Equipamento' }, { title: 'Teste' },
    { title: 'Fotos' }, { title: 'Conclusão' }];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav('/laudos')}>Voltar</Button>
        <b>Teste de Estanqueidade</b>
        {d.codigo && <Tag color="blue">{d.codigo}</Tag>}
        {d.resultado === 'estanque' && <Tag color="green">estanque</Tag>}
        {d.resultado === 'vazamento' && <Tag color="red">vazamento</Tag>}
      </Space>

      <Steps current={step} size="small" items={etapas} onChange={setStep} style={{ marginBottom: 16 }} />

      {step === 0 && <CabecalhoLaudo d={d} set={set} tipoLaudo="Teste de Estanqueidade" />}

      {step === 1 && (
        <Card title="Equipamento ensaiado" size="small">
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label>Tipo de equipamento</label>
              <Select style={{ width: '100%' }} value={d.tipo_equipamento || undefined}
                onChange={(v) => set('tipo_equipamento', v)}
                options={EQUIPAMENTOS.map((e) => ({ value: e, label: e }))} />
            </Col>
            {d.tipo_equipamento === 'Outro' && (
              <Col span={6}>
                <label>Qual equipamento</label>
                <Input value={d.tipo_equipamento_outro || ''} onChange={(e) => set('tipo_equipamento_outro', e.target.value)} />
              </Col>
            )}
            <Col span={6}>
              <label>TAG</label>
              <Input value={d.tag || ''} onChange={(e) => set('tag', e.target.value)} />
            </Col>
            <Col span={6}>
              <label>Tipo de inspeção</label>
              <Select style={{ width: '100%' }} allowClear value={d.tipo_inspecao_periodicidade || undefined}
                onChange={(v) => set('tipo_inspecao_periodicidade', v || null)}
                options={[{ value: 'inicial', label: 'Inicial' }, { value: 'periodica', label: 'Periódica' },
                  { value: 'extraordinaria', label: 'Extraordinária' }]} />
            </Col>
            {d.tipo_equipamento === 'Tubulação' && (
              <Col span={4}>
                <label>Extensão (m)</label>
                <InputNumber style={{ width: '100%' }} min={0} step={0.5} decimalSeparator=","
                  value={num(d.extensao_m)} onChange={(v) => set('extensao_m', v)} addonAfter="m" />
              </Col>
            )}
            <Col span={5}>
              <label>Nº de série</label>
              <Input value={d.numero_serie || ''} onChange={(e) => set('numero_serie', e.target.value)} />
            </Col>
            <Col span={5}>
              <label>Fabricante</label>
              <Input value={d.fabricante || ''} onChange={(e) => set('fabricante', e.target.value)} />
            </Col>
            <Col span={4}>
              <label>Ano de fabricação</label>
              <InputNumber style={{ width: '100%' }} min={1900} max={2100}
                value={num(d.ano_fabricacao)} onChange={(v) => set('ano_fabricacao', v)} />
            </Col>
            <Col span={6}>
              <label>Local de instalação</label>
              <Input value={d.local_instalacao || ''} onChange={(e) => set('local_instalacao', e.target.value)} />
            </Col>
            <Col span={6}>
              <label>Fluido de operação</label>
              <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="children"
                value={d.fluido || undefined} onChange={(v) => set('fluido', v || null)}>
                {fluidos.map((f) => <Option key={f.id} value={f.nome}>{f.nome}</Option>)}
                <Option value="outro">Outro</Option>
              </Select>
            </Col>
            {d.fluido === 'outro' && (
              <Col span={6}>
                <label>Qual fluido</label>
                <Input value={d.fluido_outro || ''} onChange={(e) => set('fluido_outro', e.target.value)} />
              </Col>
            )}
            <Col span={6}>
              <label>Material</label>
              <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="children"
                value={d.material || undefined} onChange={(v) => set('material', v || null)}>
                {materiais.map((m) => <Option key={m.id} value={m.nome}>{m.nome}{m.norma ? ' (' + m.norma + ')' : ''}</Option>)}
                <Option value="outro">Outro</Option>
              </Select>
            </Col>
            {d.material === 'outro' && (
              <Col span={6}>
                <label>Qual material</label>
                <Input value={d.material_outro || ''} onChange={(e) => set('material_outro', e.target.value)} />
              </Col>
            )}
            <Col span={4}>
              <label>PMO</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                value={num(d.pmo)} onChange={(v) => set('pmo', v)} />
            </Col>
            <Col span={2}>
              <label>Un.</label>
              <Select style={{ width: '100%' }} value={d.pmo_unidade || 'kgf'} onChange={(v) => set('pmo_unidade', v)}
                options={UNI_P.map(([v, r]) => ({ value: v, label: r }))} />
            </Col>
            <Col span={6}>
              <label>Temperatura de operação (°C)</label>
              <Input value={d.temperatura_operacao || ''} onChange={(e) => set('temperatura_operacao', e.target.value)}
                placeholder="Ex.: 0 a 50" suffix="°C" />
            </Col>
          </Row>
        </Card>
      )}

      {step === 2 && (
        <Card title="Teste de estanqueidade — método de bolha" size="small">
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label>Método</label>
              <Input value="Bolha" disabled />
              {aj('Solução formadora de bolhas aplicada em soldas, conexões, flanges e válvulas.')}
            </Col>
            <Col span={8}>
              <label>Resultado *</label>
              <Select style={{ width: '100%' }} allowClear value={d.resultado || undefined}
                onChange={(v) => set('resultado', v || null)}
                options={[{ value: 'estanque', label: 'Estanque — nenhum vazamento encontrado' },
                  { value: 'vazamento', label: 'Vazamento encontrado' }]} />
            </Col>
            <Col span={24}>
              <label>Observações {d.resultado === 'vazamento' ? '(descreva onde está o vazamento)' : ''}</label>
              <Input.TextArea rows={3} value={d.observacoes || ''} onChange={(e) => set('observacoes', e.target.value)}
                placeholder={d.resultado === 'vazamento' ? 'Ex.: vazamento na rosca da válvula de dreno do tanque.' : 'Opcional.'} />
            </Col>
            <Col span={6}>
              <label>Data do próximo teste</label>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                value={d.proximo_teste ? dayjs(String(d.proximo_teste).substring(0, 10)) : null}
                onChange={(v) => set('proximo_teste', v ? v.format('YYYY-MM-DD') : null)} />
              {aj('Vem com 1 ano a partir da data do teste.')}
            </Col>
            <Col span={18}>
              {rec ? (
                <Alert type="info" showIcon
                  message={'Validade recomendada para ' + fluidoNome + ': ' + rec.anos + ' anos'}
                  description={'Tabela de periodicidade de inspeção geral de tanques em função do produto (' + rec.grupo +
                    '). O campo continua com 1 ano; altere se for adotar o prazo recomendado.'} />
              ) : (
                <Alert type="info" showIcon
                  message={fluidoNome ? 'Sem prazo recomendado na tabela para ' + fluidoNome : 'Informe o fluido na etapa Equipamento'}
                  description="A tabela de periodicidade por produto cobre diesel, querosene, gasolina (5 anos), resíduos leves tratados e álcool (8 anos) e óleos combustíveis, lubrificantes e produtos pesados (10 anos). Tanques de troca de produtos: metade do previsto." />
              )}
            </Col>
          </Row>
        </Card>
      )}

      {step === 3 && (
        <Card title="Registro fotográfico" size="small"
          extra={<Upload beforeUpload={subir} showUploadList={false} accept="image/*" multiple>
            <Button icon={<UploadOutlined />} loading={enviando}>Adicionar foto</Button>
          </Upload>}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {(d.fotos || []).map((f, i) => (
              <div key={(f.arquivo || '') + i} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 6, width: 160 }}>
                {imgs[f.arquivo || f.caminho || String(i)] ? <img src={imgs[f.arquivo || f.caminho || String(i)]} alt={f.nome} style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 4 }} />
                  : <div style={{ height: 110, background: '#fafafa' }} />}
                <Input size="small" placeholder="Legenda" defaultValue={f.legenda || ''} style={{ marginTop: 6 }}
                  onBlur={(e) => {
                    const novas = (d.fotos || []).slice();
                    novas[i] = { ...novas[i], legenda: e.target.value };
                    setD((x) => ({ ...x, fotos: novas }));
                  }} />
                <Space size={2} style={{ marginTop: 6 }}>
                  <Button size="small" icon={f.principal ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                    title="Foto principal (capa)" onClick={() => definirCapa(i)} />
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remover(i)} />
                </Space>
              </div>))}
          </div>
          {(d.fotos || []).length === 0 && <Text type="secondary">Nenhuma foto. A marcada com estrela vai para a capa.</Text>}
        </Card>
      )}

      {step === 4 && (
        <Card title="Conclusão" size="small"
          extra={<Button icon={<FilePdfOutlined />} onClick={gerarPdf}>Gerar PDF</Button>}>
          {!d.resultado && <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="Escolha o resultado na etapa Teste antes de gerar o PDF." />}
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label>Status do laudo</label>
              <Select style={{ width: '100%' }} value={d.status || 'rascunho'} onChange={(v) => set('status', v)}
                options={[{ value: 'rascunho', label: 'Rascunho' }, { value: 'concluido', label: 'Concluído' }]} />
            </Col>
            <Col span={24}>
              <label>Conclusão</label>
              <Input.TextArea rows={5} value={d.conclusao || ''} onChange={(e) => set('conclusao', e.target.value)}
                placeholder="Deixe em branco para usar o texto padrão, conforme o resultado (estanque ou com vazamento)." />
            </Col>
          </Row>
        </Card>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button disabled={step === 0} onClick={() => setStep(step - 1)}>Anterior</Button>
          <Button disabled={step === etapas.length - 1} onClick={() => setStep(step + 1)}>Próxima</Button>
        </Space>
        <Space>
          {step === etapas.length - 1 && <Button icon={<FilePdfOutlined />} onClick={gerarPdf}>Gerar PDF</Button>}
          <Button type="primary" icon={<SaveOutlined />} loading={salvando} onClick={() => salvar()}>Salvar</Button>
        </Space>
      </div>
    </div>
  );
}
